import {
  getHreflangHandleForLanguageOption,
  rewriteProductHandleInPath,
} from '@nulo/hreflang-handles';

const DESKTOP_BREAKPOINT = 1024;

/**
 * @param {string | undefined | null} value
 * @returns {string}
 */
function normalizePrefix(value) {
  if (!value || value === '/') return '';
  const withSlash = value.startsWith('/') ? value : `/${value}`;
  return withSlash.replace(/\/$/, '');
}

/**
 * @param {string} pathname
 * @param {string[]} prefixes
 * @returns {string}
 */
function stripMarketPrefix(pathname, prefixes) {
  const sorted = [...prefixes]
    .map(normalizePrefix)
    .filter(Boolean)
    .sort((a, b) => b.length - a.length);

  for (const prefix of sorted) {
    if (pathname === prefix) return '/';
    if (pathname.startsWith(`${prefix}/`)) {
      return pathname.slice(prefix.length) || '/';
    }
  }
  return pathname;
}

/**
 * @typedef {{ id: string, flag: string, name: string, language: string, link: string, rootUrl?: string, sameStorefront?: boolean }} RegionOption
 */

/**
 * @param {RegionOption[]} options
 * @param {string} [currentLocaleRoot]
 * @returns {string[]}
 */
function collectMarketPrefixes(options, currentLocaleRoot = '') {
  const prefixes = new Set();
  const current = normalizePrefix(currentLocaleRoot);
  if (current) prefixes.add(current);

  for (const option of options) {
    const rootUrl = normalizePrefix(option.rootUrl);
    if (rootUrl) prefixes.add(rootUrl);

    // Only infer path prefixes from this storefront (avoid treating /fr on nulo.ca as a prefix).
    if (!option.sameStorefront) continue;

    try {
      const path = normalizePrefix(new URL(option.link, window.location.origin).pathname);
      if (path) prefixes.add(path);
    } catch {
      // Ignore invalid option links.
    }
  }

  return [...prefixes];
}

/**
 * Prefer explicit locale_root; otherwise use the pathname from store_url when it
 * looks like a market subfolder (e.g. https://shop.com/no-no → /no-no).
 *
 * @param {RegionOption} option
 * @returns {string}
 */
function resolveMarketRoot(option) {
  const fromSetting = normalizePrefix(option.rootUrl);
  if (fromSetting) return fromSetting;

  if (!option.sameStorefront) return '';

  try {
    return normalizePrefix(new URL(option.link, window.location.origin).pathname);
  } catch {
    return '';
  }
}

/**
 * US / CA Hydrogen storefronts — path rewriting applies only here.
 *
 * @param {RegionOption} option
 * @returns {boolean}
 */
function isHydrogenStore(option) {
  const id = (option.id || '').toLowerCase();
  return id.startsWith('us-') || id.startsWith('ca-');
}

/**
 * Map a Shopify EU path to the equivalent Hydrogen (nulo.com / nulo.ca) path.
 *
 * @param {string} pathname - Locale-stripped pathname
 * @param {string} [parentCollectionHandle]
 * @returns {string}
 */
function mapHydrogenPath(pathname, parentCollectionHandle = '') {
  const path = pathname.replace(/\/+$/, '') || '/';

  if (path === '/blogs/ambassador-stories') {
    return '/be-inspired';
  }

  const ambassadorArticle = path.match(/^\/blogs\/ambassador-stories\/([^/]+)$/);
  if (ambassadorArticle) {
    return `/ambassador-stories/${ambassadorArticle[1]}`;
  }

  if (path.startsWith('/pages/')) {
    const rest = path.slice('/pages/'.length);
    return rest ? `/${rest}` : '/';
  }

  const collectionMatch = path.match(/^\/collections\/([^/]+)$/);
  if (collectionMatch) {
    const handle = collectionMatch[1];
    if (handle === 'for-dogs' || handle === 'for-cats') {
      return `/collections/${handle}`;
    }
    if (parentCollectionHandle && parentCollectionHandle !== handle) {
      return `/collections/${parentCollectionHandle}/${handle}`;
    }
    return `/collections/${handle}`;
  }

  return path;
}

/**
 * External Hydrogen (US, CA): rewrite path for Oxygen/Hydrogen routes.
 * Path is joined to the store origin only (no /en or /fr prefix) — CA falls
 * back to EN when locale prefix is omitted.
 * Same storefront markets (UK / IT / ES / NO / SE / FI): keep the current page path
 * via the `same_storefront` region setting.
 * Other external hosts: configured URL as-is.
 *
 * On PDPs, remaps `/products/:handle` (and collection PDP aliases) via
 * `custom.hreflang_handles` before navigation — never appends switch flags.
 *
 * @param {RegionOption} option
 * @param {string[]} marketPrefixes
 * @param {{ parentCollectionHandle?: string, hreflangHandles?: string | null }} [context]
 * @returns {string}
 */
function buildStoreUrl(option, marketPrefixes, context = {}) {
  const url = new URL(option.link, window.location.origin);
  const isSameHost = url.hostname === window.location.hostname;
  let path = stripMarketPrefix(window.location.pathname, marketPrefixes);

  if (context.hreflangHandles && /\/products\/[^/]+/.test(path)) {
    const targetHandle = getHreflangHandleForLanguageOption(
      context.hreflangHandles,
      option,
    );
    if (targetHandle) {
      path = rewriteProductHandleInPath(path, targetHandle);
    }
  }

  if (isHydrogenStore(option)) {
    const mapped = mapHydrogenPath(path, context.parentCollectionHandle || '');
    url.pathname = mapped === '/' ? '/' : mapped;
    url.search = window.location.search;
    url.hash = window.location.hash;
    return url.toString();
  }

  if (!option.sameStorefront && !isSameHost) {
    return url.toString();
  }

  const marketRoot = resolveMarketRoot(option);

  if (marketRoot) {
    url.pathname = marketRoot + (path === '/' ? '' : path);
  } else {
    url.pathname = path || '/';
  }

  url.search = window.location.search;
  url.hash = window.location.hash;
  return url.toString();
}

/**
 * @extends {HTMLElement}
 */
class NuloLanguageDropdown extends HTMLElement {
  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {RegionOption[]} */
  #options = [];

  /** @type {RegionOption | null} */
  #selected = null;

  /** @type {string[]} */
  #marketPrefixes = [];

  /** @type {string | null} */
  #hreflangHandles = null;

  connectedCallback() {
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.#parseOptions();
    this.#renderSelected();
    this.#renderOptions();

    const toggle = this.querySelector('[data-toggle]');
    const panel = this.querySelector('[data-panel]');

    toggle?.addEventListener('click', () => this.#togglePanel(), { signal });
    document.addEventListener('mousedown', (event) => this.#handleClickOutside(event), { signal });
    document.addEventListener('touchstart', (event) => this.#handleClickOutside(event), { signal });
    document.addEventListener('keydown', (event) => this.#handleKeydown(event), { signal });

    panel?.addEventListener('click', (event) => this.#handleOptionClick(event), { signal });
    panel?.addEventListener('keydown', (event) => this.#handleOptionKeydown(event), { signal });
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = null;
  }

  #parseOptions() {
    this.#options = this.#readOptionsScript('[data-region-options]').sort(
      (a, b) => {
        const byName = a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
        if (byName !== 0) return byName;
        return a.language.localeCompare(b.language, undefined, { sensitivity: 'base' });
      },
    );
    this.#marketPrefixes = collectMarketPrefixes(
      this.#options,
      this.dataset.currentLocaleRoot || '',
    );
    this.#hreflangHandles = this.#readHreflangHandles();

    const selectedId = this.dataset.selectedId;
    this.#selected =
      this.#options.find((option) => option.id === selectedId) ??
      this.#options[0] ??
      null;
  }

  /**
   * @returns {string | null}
   */
  #readHreflangHandles() {
    const script = this.querySelector('[data-hreflang-handles]');
    if (!script?.textContent?.trim()) return null;

    try {
      const value = JSON.parse(script.textContent.trim());
      return typeof value === 'string' && value.trim() ? value.trim() : null;
    } catch {
      return null;
    }
  }

  /**
   * @param {string} selector
   * @returns {RegionOption[]}
   */
  #readOptionsScript(selector) {
    const script = this.querySelector(selector);
    if (!script?.textContent?.trim()) return [];

    try {
      const options = JSON.parse(script.textContent.trim());
      return Array.isArray(options) ? options : [];
    } catch {
      return [];
    }
  }

  /**
   * @param {string} url
   * @returns {string}
   */
  #normalizeAssetUrl(url) {
    if (!url) return url;
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    if (url.startsWith('//')) return `https:${url}`;
    return url;
  }

  #renderSelected() {
    if (!this.#selected) return;

    const flagEl = this.querySelector('[data-selected-flag]');
    const labelEl = this.querySelector('[data-selected-label]');

    if (flagEl) {
      const flagUrl = this.#normalizeAssetUrl(this.#selected.flag);
      flagEl.innerHTML = flagUrl
        ? `<img src="${flagUrl}" alt="" width="30" height="16" loading="lazy">`
        : '';
    }

    if (labelEl) {
      labelEl.textContent = `${this.#selected.name}/${this.#selected.language}`;
    }
  }

  #renderOptions() {
    const list = this.querySelector('[data-options-list]');
    if (!list || !this.#selected) return;

    const filtered = this.#options.filter((option) => option.id !== this.#selected?.id);

    list.innerHTML = filtered
      .map((option) => {
        const flagUrl = this.#normalizeAssetUrl(option.flag);
        const flagMarkup = flagUrl
          ? `<img src="${flagUrl}" alt="" width="30" height="16" loading="lazy">`
          : '';

        return `
          <li class="nulo-lang-dropdown__item" role="presentation">
            <button
              type="button"
              class="nulo-lang-dropdown__option"
              role="option"
              data-option-id="${option.id}"
              tabindex="0"
            >
              <span class="nulo-lang-dropdown__option-flag">
                ${flagMarkup}
              </span>
              <span class="nulo-lang-dropdown__option-label">${option.name}/${option.language}</span>
            </button>
          </li>
        `;
      })
      .join('');
  }

  #isDesktop() {
    return window.matchMedia(`(min-width: ${DESKTOP_BREAKPOINT}px)`).matches;
  }

  #togglePanel() {
    const panel = this.querySelector('[data-panel]');
    const toggle = this.querySelector('[data-toggle]');
    if (!panel || !toggle) return;

    const isOpen = !panel.hidden;
    panel.hidden = isOpen;
    toggle.setAttribute('aria-expanded', String(!isOpen));
    this.classList.toggle('is-open', !isOpen);
  }

  #closePanel() {
    const panel = this.querySelector('[data-panel]');
    const toggle = this.querySelector('[data-toggle]');
    if (!panel || !toggle) return;

    panel.hidden = true;
    toggle.setAttribute('aria-expanded', 'false');
    this.classList.remove('is-open');
  }

  /**
   * @param {Event} event
   */
  #handleClickOutside(event) {
    if (!this.classList.contains('is-open')) return;
    if (event.target instanceof Node && this.contains(event.target)) return;
    this.#closePanel();
  }

  /**
   * @param {KeyboardEvent} event
   */
  #handleKeydown(event) {
    if (!this.classList.contains('is-open')) return;

    if (event.key === 'Escape') {
      this.#closePanel();
      this.querySelector('[data-toggle]')?.focus();
    }

    if (this.#isDesktop() && event.key === 'ArrowDown') {
      event.preventDefault();
      this.querySelector('.nulo-lang-dropdown__option')?.focus();
    }
  }

  /**
   * @param {MouseEvent} event
   */
  #handleOptionClick(event) {
    const button = event.target instanceof Element ? event.target.closest('[data-option-id]') : null;
    if (!button) return;

    const option = this.#options.find((item) => item.id === button.dataset.optionId);
    if (!option) return;

    window.location.href = buildStoreUrl(option, this.#marketPrefixes, {
      parentCollectionHandle: this.dataset.parentCollectionHandle || '',
      hreflangHandles: this.#hreflangHandles,
    });
  }

  /**
   * @param {KeyboardEvent} event
   */
  #handleOptionKeydown(event) {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    const option = target.closest('[data-option-id]');
    if (!option) return;

    const options = Array.from(this.querySelectorAll('.nulo-lang-dropdown__option'));
    const index = options.indexOf(option);
    if (index < 0) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      options[index + 1]?.focus();
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (index > 0) {
        options[index - 1]?.focus();
      } else {
        this.querySelector('[data-toggle]')?.focus();
      }
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      option.click();
    }
  }
}

if (!customElements.get('nulo-language-dropdown')) {
  customElements.define('nulo-language-dropdown', NuloLanguageDropdown);
}
